import { Actor, CollisionType, Color, EmitterType, Engine, ParticleEmitter, Scene, vec } from 'excalibur';
import { Elf } from './actors/elf';
import { Santa } from './actors/santa';
import { Background } from './actors/background';
import { COLLISION_GROUPS } from './collision-groups';
import { CONFIG, LevelKey } from './config';
import { Workshop } from './actors/workshop';
import { Resources } from './resources';

const levelKey = `level${CONFIG.firstLevel}` as LevelKey;

export class Level extends Scene {
  private background: Background;
  private workshop: Workshop;

  private wallLeft!: Actor;
  private wallRight!: Actor;
  private santa!: Santa;
  private elves: Array<Elf> = [];
  private emitters: Array<ParticleEmitter> = [];

  constructor() {
    super();
    this.background = new Background();
    this.workshop = new Workshop();
  }

  override onInitialize(_engine: Engine): void {
    const { room } = CONFIG.levels[levelKey];

    const leftBound = room.left;
    const rightBound = room.left + this.workshop.width - 4;

    this.add(this.background);

    this.workshop.pos = vec(room.left, room.top);
    this.add(this.workshop);

    Resources.Level1.addToScene(this);

    this.wallLeft = this.createWall(leftBound, room.top, 'RoomWallLeft');
    this.add(this.wallLeft);

    this.wallRight = this.createWall(rightBound, room.top, 'RoomWallRight');
    this.add(this.wallRight);

    this.santa = new Santa(levelKey);
    this.add(this.santa);

    this.createEmitter();
    for (const emitter of this.emitters) {
      this.add(emitter);
    }

    for (let i = 0; i < CONFIG.elfCount; i++) {
      const elf = new Elf(levelKey, { left: leftBound + 30, right: rightBound - 30 });
      this.elves.push(elf);
      this.add(elf);
    }

    this.camera.pos = vec(CONFIG.screen.width / 2, CONFIG.screen.height / 2);
    this.camera.zoom = CONFIG.scale;
  }

  private launchElf(elf: Elf): void {
    const random = 2 + Math.random() * 2;
    const facing = this.santa.facingDirection();
    const xVector = (3 - elf.pos.x) * random * -facing;
    const yVector = (3 - elf.pos.y) * random;

    elf.vel.x = xVector;
    elf.vel.y = yVector;

    this.camera.shake(5, 5, 500);
  }

  public onPreUpdate(): void {
    if (!this.santa.isStriking()) {
      return;
    }

    const santaBounds = this.santa.collider.bounds;

    for (const elf of this.elves) {
      const overlap = santaBounds.intersect(elf.collider.bounds);
      if (overlap && (overlap.x !== 0 || overlap.y !== 0)) {
        this.launchElf(elf);
      }
    }
  }

  public onPostDraw(): void {
    const workingElves = this.elves.filter((elf) => elf.working).length;

    for (const emitter of this.emitters) {
      emitter.emitRate = workingElves * 0.5;
    }
  }

  private createWall(x: number, y: number, name: string): Actor {
    const wall = new Actor({
      name,
      pos: vec(x, y + 24),
      anchor: vec(0, 0),
      width: 4,
      height: 60,
      collisionType: CollisionType.Fixed,
      collisionGroup: COLLISION_GROUPS.world,
      color: Color.Transparent,
      z: 12,
    });
    wall.body.useGravity = false;
    return wall;
  }

  private createEmitter(): void {
    const { room } = CONFIG.levels[levelKey];

    const config = {
      x: room.left + this.workshop.width / 2 - 20,
      y: room.top + this.workshop.height - 10,
      z: 500,
      width: 40,
      height: 1,
      emitterType: EmitterType.Rectangle,
      isEmitting: true,
      emitRate: 0,
    };
    const particle = {
      minSpeed: 100,
      maxSpeed: 250,
      minAngle: (Math.PI / 180) * 250,
      maxAngle: (Math.PI / 180) * 290,
      life: 2000,
      minSize: 2,
      maxSize: 10,
      startSize: 2,
      randomRotation: true,
      endSize: 10,
      acc: vec(0, 200),
    };

    this.emitters = [
      new ParticleEmitter({
        ...config,
        particle: {
          ...particle,
          graphic: Resources.bear.toSprite(),
        },
      }),
      new ParticleEmitter({
        ...config,
        particle: {
          ...particle,
          graphic: Resources.ball.toSprite(),
        },
      }),
      new ParticleEmitter({
        ...config,
        particle: {
          ...particle,
          graphic: Resources.candyCane.toSprite(),
        },
      }),
      new ParticleEmitter({
        ...config,
        particle: {
          ...particle,
          graphic: Resources.gift.toSprite(),
        },
      }),
      new ParticleEmitter({
        ...config,
        particle: {
          ...particle,
          graphic: Resources.star.toSprite(),
        },
      }),
    ];
  }
}
